import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { trpc } from '../../trpc/client';
import { format } from 'date-fns';
import { Calendar, User, ArrowLeft, Tag, Share2 } from 'lucide-react';

import { Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown'; // Or verify if standard text is desired. Assuming text for now or simple HTML render.
// Note: If using a rich text editor that saves HTML, use dangerouslySetInnerHTML safely or a parser.
// For now, let's assume raw text/markdown or basic whitespace handling.

// Safely rendering HTML content (assuming backend sanitizes or using trusted admin input)
const SafeHTML = ({ content }: { content: string }) => {
    return (
        <div
            className="prose prose-lg prose-blue max-w-none text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: content.replace(/\n/g, '<br />') }}
        />
    );
};

const BlogPostPage = () => {
    const { slug } = useParams<{ slug: string }>();
    const { data: post, isLoading, error } = trpc.blog.getPostBySlug.useQuery(
        { slug: slug! },
        { enabled: !!slug, retry: false }
    );

    if (isLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary-600" /></div>;

    if (error || !post) {
        return (
            <div className="min-h-screen bg-background flex flex-col">
                <div className="flex-grow flex flex-col items-center justify-center text-center px-4">
                    <h1 className="text-4xl font-black text-foreground mb-4">Post Not Found</h1>
                    <p className="text-muted-foreground mb-8">The article you are looking for does not exist or has been removed.</p>
                    <Link to="/blog" className="text-primary font-semibold hover:underline flex items-center gap-2">
                        <ArrowLeft className="w-5 h-5" /> Back to Blog
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background flex flex-col">

            <main className="flex-grow pt-24 pb-20">
                <article className="max-w-4xl mx-auto px-4">

                    {/* Back Link */}
                    <Link to="/blog" className="inline-flex items-center text-muted-foreground hover:text-primary mb-8 transition-colors font-medium">
                        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Articles
                    </Link>

                    {/* Header */}
                    <header className="mb-12 text-center">
                        {post.tags.length > 0 && (
                            <div className="flex justify-center gap-2 mb-6">
                                {post.tags.map(tag => (
                                    <span key={tag} className="px-3 py-1 bg-primary/10 text-primary text-sm font-bold rounded-full border border-primary/20">
                                        #{tag}
                                    </span>
                                ))}
                            </div>
                        )}

                        <h1 className="text-4xl md:text-6xl font-black text-foreground mb-6 font-display leading-tight tracking-tight">
                            {post.title}
                        </h1>

                        <div className="flex items-center justify-center text-muted-foreground gap-6 text-sm md:text-base font-medium">
                            <div className="flex items-center gap-2">
                                {post.author.profile?.profilePicturePath ? (
                                    <img src={post.author.profile.profilePicturePath} className="w-10 h-10 rounded-full object-cover ring-2 ring-background" alt="" />
                                ) : (
                                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center ring-2 ring-background">
                                        <User className="w-5 h-5 text-muted-foreground" />
                                    </div>
                                )}
                                <span className="font-bold text-foreground">
                                    {post.author.profile?.fullName || post.author.username || 'Admin'}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5 opacity-70" />
                                {format(new Date(post.createdAt), 'MMMM d, yyyy')}
                            </div>
                        </div>
                    </header>

                    {/* Cover Image */}
                    {post.coverImage && (
                        <div className="mb-12 rounded-2xl overflow-hidden shadow-lg aspect-video relative group">
                            <img
                                src={post.coverImage}
                                alt={post.title}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    )}

                    {/* Content */}
                    <div className="max-w-3xl mx-auto">
                        <div className="prose prose-lg dark:prose-invert prose-headings:font-bold prose-headings:text-foreground prose-p:text-foreground/90 prose-a:text-primary prose-strong:text-foreground prose-ul:text-foreground/90 prose-ol:text-foreground/90 prose-li:text-foreground/90 prose-blockquote:text-muted-foreground prose-img:rounded-xl mx-auto leading-relaxed">
                            <ReactMarkdown>
                                {post.content}
                            </ReactMarkdown>
                        </div>
                    </div>

                    {/* Share / Footer of Article */}
                    <div className="max-w-3xl mx-auto mt-16 pt-8 border-t border-border flex flex-col md:flex-row gap-4 justify-between items-center">
                        <div className="text-muted-foreground font-medium">
                            Share this article
                        </div>
                        <div className="flex gap-4">
                            <button className="p-3 rounded-full bg-secondary hover:bg-secondary/80 text-foreground transition-colors">
                                <Share2 className="w-5 h-5" />
                            </button>
                            {/* Add social links here */}
                        </div>
                    </div>

                </article>
            </main>

        </div>
    );
};

export default BlogPostPage;
